
import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Square, Clock, Sandwich } from 'lucide-react';
import { formatTime } from '@/utils/timeUtils';

interface TimeDisplayProps {
  elapsedTime: number;
  isTracking: boolean;
  onStart: () => void;
  onStop: () => void;
  canStart: boolean;
  isLoading?: boolean;
  lunchEnd?: Date | null;
}

const TimeDisplay: React.FC<TimeDisplayProps> = React.memo(({
  elapsedTime,
  isTracking,
  onStart,
  onStop,
  canStart,
  isLoading = false,
  lunchEnd = null
}) => {
  // Memoization du temps formaté
  const formattedTime = useMemo(() => {
    return formatTime(elapsedTime);
  }, [elapsedTime]);

  // Choix dynamique de l'icône selon l'état (suivi en cours, pause dîner, inactif)
  const StatusIcon = useMemo(() => {
    if (isTracking) return Clock;
    if (lunchEnd) return Sandwich;
    return Clock;
  }, [isTracking, lunchEnd]);

  // Memoization des classes de bouton
  const startButtonClasses = useMemo(() => {
    return `w-full h-16 text-lg font-semibold transition-all duration-200 ${
      canStart && !isLoading
        ? 'bg-green-600 hover:bg-green-700 text-white'
        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
    }`;
  }, [canStart, isLoading]);

  const stopButtonClasses = useMemo(() => {
    return `w-full h-16 text-lg font-semibold bg-red-600 hover:bg-red-700 text-white transition-all duration-200 ${
      isLoading ? 'opacity-50 cursor-not-allowed' : ''
    }`;
  }, [isLoading]);

  const statusText = useMemo(() => {
    if (isTracking) return 'Temps en cours...';
    if (lunchEnd) {
      return `Pause dîner… reprise à ${lunchEnd.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    }
    return 'Aucun shift actif';
  }, [isTracking, lunchEnd]);

  return (
    <div className="space-y-4" role="region" aria-label="Affichage du temps">
      {/* Affichage du temps */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <StatusIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
          <h2 className="text-2xl font-bold text-gray-900">
            {isTracking ? formattedTime : ''}
          </h2>
        </div>
        <p className="text-sm text-gray-600">{statusText}</p>
      </div>

      {/* Boutons d'action */}
      <div className="space-y-3">
        {!isTracking ? (
          <Button
            onClick={onStart}
            disabled={!canStart || isLoading}
            className={startButtonClasses}
            aria-label="Démarrer le suivi de temps"
            aria-describedby={!canStart ? 'start-disabled-reason' : undefined}
          >
            <Play className="h-5 w-5 mr-2" aria-hidden="true" />
            {isLoading ? 'Démarrage...' : 'Démarrer'}
          </Button>
        ) : (
          <Button
            onClick={onStop}
            disabled={isLoading}
            className={stopButtonClasses}
            aria-label="Arrêter le suivi de temps"
          >
            <Square className="h-5 w-5 mr-2" aria-hidden="true" />
            {isLoading ? 'Arrêt...' : 'Arrêter'}
          </Button>
        )}

        {/* Message d'aide pour l'accessibilité */}
        {!canStart && !isTracking && (
          <p 
            id="start-disabled-reason" 
            className="text-xs text-gray-500 text-center"
            role="status"
          >
            Sélectionnez un projet pour commencer
          </p>
        )}
      </div>


    </div>
  );
});

TimeDisplay.displayName = 'TimeDisplay';

export default TimeDisplay;
